/**
 * Raising a charge outside the rent schedule.
 *
 * Rent bills itself from the tenancy. Everything else does not: a service charge levied on the
 * estate this quarter, a bill for a broken door, a fee agreed halfway through a term. Those
 * arrive whenever they arrive, in any number, and until now the only way to bill one was to
 * attach a line to an invoice that already existed — which meant burying a new charge inside a
 * rent demand the tenant had already been sent.
 *
 * **Move-in charges are not this.** Agency fee, caution deposit and legal fee belong to the
 * tenancy and are billed once, on the commencement invoice, by the schedule — see
 * `packages/core/src/billing.ts`. They are charged again when a new tenancy starts, including
 * for a tenant who left and came back, because they attach to the agreement rather than to the
 * person. This screen is for everything that is not that.
 */

import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { CHARGE_TYPES, parseMoney } from "@rentorng/core";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { useFormat, useToday } from "@/lib/format";
import { useSession } from "@/lib/session";
import { resolveParty, resolveUnit, searchParties, searchUnits } from "@/lib/pickers";
import { SearchSelect } from "@/components/search-select";
import { Alert, Button, Card, CardTitle, Field, Input, PageHeader, Select, Textarea } from "@/components/ui";
import { MoneyInput } from "@/components/money-input";

interface Line {
  chargeType: string;
  description: string;
  amount: string;
}

/** The types that make sense to raise on their own. Rent comes from a tenancy, never from here. */
const AD_HOC_TYPES = CHARGE_TYPES.filter((type) => type.value !== "rent" && type.value !== "late_fee");

const BLANK: Line = { chargeType: "service_charge", description: "", amount: "" };

export function InvoiceNewPage() {
  const navigate = useNavigate();
  const today = useToday();
  const fmt = useFormat();
  const { vocab } = useSession();

  const [partyId, setPartyId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [lines, setLines] = useState<Line[]>([{ ...BLANK }]);

  const total = lines.reduce((sum, line) => sum + (parseMoney(line.amount) ?? 0), 0);

  const create = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ id: string }>("/invoices", input),
    invalidates: [["invoices"], ["dashboard"], ["party"]],
    onSuccess: (data) => navigate(`/app/invoices/${data.id}`),
  });

  const setLine = (index: number, patch: Partial<Line>) =>
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    create.mutate({
      partyId,
      unitId: unitId || null,
      issueDate: String(form.get("issueDate") ?? today),
      dueDate: String(form.get("dueDate") ?? today),
      notes: String(form.get("notes") ?? "") || null,
      lines: lines
        .filter((line) => line.description.trim() && (parseMoney(line.amount) ?? 0) > 0)
        .map((line) => ({
          chargeType: line.chargeType,
          description: line.description.trim(),
          amount: parseMoney(line.amount) ?? 0,
        })),
    });
  };

  const ready = Boolean(partyId) && total > 0;

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="Raise a charge"
        back={{ to: "/app/invoices", label: titleCaseSafe(vocab.invoices) }}
        subtitle="For anything outside the rent schedule — a service charge, a recharge, a one-off fee. Raise as many as you need."
      />

      <Card>
        <form onSubmit={submit} className="space-y-4">
          {create.errorMessage ? <Alert tone="danger">{create.errorMessage}</Alert> : null}

          <Field label="Who is being charged" htmlFor="partyId" required>
            <SearchSelect
              id="partyId"
              value={partyId}
              onChange={(next) => setPartyId(next)}
              search={searchParties()}
              resolve={resolveParty}
              placeholder="Search for a client…"
              emptyMessage="No client matched."
            />
          </Field>

          <Field
            label={`${titleCaseSafe(vocab.unit)} it relates to`}
            htmlFor="unitId"
            hint="Optional, but it puts the charge on the right property in every report."
          >
            <SearchSelect
              id="unitId"
              value={unitId}
              onChange={(next) => setUnitId(next)}
              search={searchUnits}
              resolve={resolveUnit}
              placeholder="Not linked"
              emptyMessage="Nothing matched."
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Issued" htmlFor="issueDate">
              <Input id="issueDate" name="issueDate" type="date" defaultValue={today} />
            </Field>
            <Field label="Due" htmlFor="dueDate" hint="When you expect the money.">
              <Input id="dueDate" name="dueDate" type="date" defaultValue={today} />
            </Field>
          </div>

          <div>
            <CardTitle hint="Every line appears separately on the invoice and the receipt.">What for</CardTitle>

            <div className="space-y-3">
              {lines.map((line, index) => (
                <div key={index} className="grid gap-2 sm:grid-cols-[10rem_1fr_8rem_auto]">
                  <Select
                    value={line.chargeType}
                    onChange={(event) => setLine(index, { chargeType: event.target.value })}
                    aria-label="Kind of charge"
                  >
                    {AD_HOC_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                  <Input
                    value={line.description}
                    onChange={(event) => setLine(index, { description: event.target.value })}
                    placeholder="Estate levy, second quarter"
                    aria-label="Description"
                  />
                  <MoneyInput
                    value={line.amount}
                    onChange={(amount) => setLine(index, { amount })}
                    placeholder="50,000"
                    aria-label="Amount"
                  />
                  <Button
                    variant="ghost"
                    onClick={() => setLines((current) => (current.length === 1 ? current : current.filter((_, i) => i !== index)))}
                    disabled={lines.length === 1}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>

            <Button variant="secondary" className="mt-3" onClick={() => setLines((current) => [...current, { ...BLANK }])}>
              Add another line
            </Button>
          </div>

          <Field label="Note" htmlFor="notes" hint="Shown on the invoice.">
            <Textarea id="notes" name="notes" rows={2} placeholder="Anything that should appear on the invoice." />
          </Field>

          <div className="flex items-center justify-between gap-3 border-t border-ink-200 pt-4">
            <div>
              <p className="eyebrow">Total</p>
              <p className="tabular text-lg font-semibold">{fmt.money(total)}</p>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={!ready || create.isPending}>
                {create.isPending ? "Raising…" : "Raise the charge"}
              </Button>
              <Button variant="secondary" onClick={() => navigate("/app/invoices")}>
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
}

/** The vocabulary is lower-case; a heading is not. */
function titleCaseSafe(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
